"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const CompanyAnnouncementContext = createContext();

export const CompanyAnnouncementProvider = ({ children }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Token
  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // -----------------------------------
  // FETCH ALL ANNOUNCEMENTS
  // -----------------------------------
  const fetchAnnouncements = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/company-announcements`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch announcements");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setAnnouncements(data.data || []);
      } else {
        setError(data.message || "Failed to fetch announcements");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching announcements");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------
  // CREATE ANNOUNCEMENT
  // -----------------------------------
  const createAnnouncement = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/company-announcements`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to create announcement",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchAnnouncements();
        toast.success(data.message || "Announcement created successfully");

        return {
          success: true,
          message: data.message || "Announcement created successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create announcement",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error creating announcement",
      };
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------
  // UPDATE ANNOUNCEMENT
  // -----------------------------------
  const updateAnnouncement = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/company-announcements/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update announcement",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchAnnouncements();
        toast.success(data.message || "Announcement updated successfully");

        return {
          success: true,
          message: data.message || "Announcement updated successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update announcement",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating announcement",
      };
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------
  // UPDATE STATUS
  // -----------------------------------
  const updateAnnouncementStatus = async (id, status, update_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(
        `${BASE_URL}/company-announcements/${id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status, update_audit_id }),
        }
      );

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchAnnouncements();
        toast.success(data.message || "Status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating status",
      };
    }
  };

  // -----------------------------------
  // DELETE (SOFT DELETE)
  // -----------------------------------
  const deleteAnnouncement = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/company-announcements/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to delete announcement",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchAnnouncements();
        toast.success(data.message || "Deleted successfully");

        return {
          success: true,
          message: data.message || "Deleted successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete announcement",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting announcement",
      };
    }
  };

  // -----------------------------------
  // LOAD ON START
  // -----------------------------------
  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchAnnouncements();
    }
  }, []);

  return (
    <CompanyAnnouncementContext.Provider
      value={{
        announcements,
        loading,
        error,
        fetchAnnouncements,
        createAnnouncement,
        updateAnnouncement,
        updateAnnouncementStatus,
        deleteAnnouncement,
      }}
    >
      {children}
    </CompanyAnnouncementContext.Provider>
  );
};

export const useCompanyAnnouncements = () =>
  useContext(CompanyAnnouncementContext);